import { lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import App from './App';
import AppLayout from './components/AppLayout/AppLayout';

// Pages
const AdminPage = lazy(() => import('./pages/AdminPage/AdminPage'));
const AssetManagerPage = lazy(() => import('./legacy/components/asset-manager-container'));
const ClientDetailsView = lazy(
  () => import('./legacy/components/CustomerAccountsV2/ClientDetailView')
);
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const DashboardContainer = lazy(() => import('./pages/NewDashboard/DashboardContainer'));
const DealerDetailView = lazy(
  () => import('./legacy/components/DealerAccountsV2/DealerDetailView')
);
const DealersPage = lazy(() => import('./pages/DealersPage'));
const MeasureModelPage = lazy(() => import('./pages/MeasureModelPage'));
const MeasureModelDetailPage = lazy(
  () => import('./pages/MeasureModelDetailPage/MeasureModelDetailPage')
);
const Monitor = lazy(() => import('./pages/MonitorPage'));
const MonitorDetailPage = lazy(() => import('./pages/MonitorDetailPage/MonitorDetailPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ProfileSettingsPage = lazy(() => import('./legacy/components/profile-settings-container'));
const ProjectPage = lazy(() => import('./pages/ProjectsPage/ProjectsPage'));
const SimulatorPage = lazy(() => import('./pages/SimulatorPage/SimulatorPage'));
const SimulatorConfigDetail = lazy(() => import('./pages/SimulatorPage/SimulatorConfigDetail'));
const StyleGuidePage = lazy(() => import('./pages/StyleGuidePage'));
const TestPage = lazy(() => import('./pages/TestPage'));

// Auth
const LoginPage = lazy(() => import('./pages/SignInPage/SignInPage'));
const ResetPage = lazy(() => import('./pages/SignInPage/SignInPage'));

// Error
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

export const lazyRoute =
  <T = any>(importFn: () => Promise<T>, componentName: keyof T) =>
  async () => {
    const module = await importFn();
    return { Component: module[componentName] };
  };

export const appRoutes = [
  {
    path: '/',
    Component: DashboardContainer,
    protected: true,
  },
  {
    path: '/dashboard/:id',
    Component: DashboardContainer,
    protected: true,
  },
  {
    path: '/dashboard',
    Component: DashboardContainer,
    protected: true,
  },
  {
    path: '/new-dashboard/:id?',
    Component: DashboardContainer,
    protected: true,
  },
  {
    path: '/customers',
    Component: CustomersPage,
    protected: true,
  },
  {
    path: '/customers/:id',
    Component: ClientDetailsView,
    protected: true,
  },
  {
    path: '/dealers',
    Component: DealersPage,
    protected: true,
  },
  {
    path: '/dealers/:id',
    Component: DealerDetailView,
    protected: true,
  },
  {
    path: 'measure-model',
    Component: MeasureModelPage,
    protected: true,
  },
  {
    path: 'measure-model/:id',
    Component: MeasureModelDetailPage,
    protected: true,
  },
  {
    path: 'monitor',
    Component: Monitor,
    protected: true,
  },
  {
    path: 'monitor/:id',
    Component: MonitorDetailPage,
    protected: true,
  },
  {
    path: 'asset-manager/:segment?',
    Component: AssetManagerPage,
    protected: true,
  },
  {
    path: 'admin',
    Component: AdminPage,
    protected: true,
  },
  {
    path: 'user-settings',
    Component: ProfileSettingsPage,
    protected: true,
  },
  {
    path: 'new-project',
    Component: ProjectPage,
    protected: true,
  },
  {
    path: 'simulator',
    Component: SimulatorPage,
    protected: true,
    policies: ['Simulator_CanAccessView'],
  },
  {
    path: 'simulator/:id',
    Component: SimulatorConfigDetail,
    protected: true,
    policies: ['Simulator_CanAccessView'],
  },
  {
    path: 'style-guide',
    Component: StyleGuidePage,
    protected: true,
  },
  {
    path: 'test',
    Component: TestPage,
    protected: true,
  },
  {
    path: 'login',
    Component: LoginPage,
  },
  {
    path: 'reset-password',
    Component: ResetPage,
  },
];

export const router = createBrowserRouter(
  [
    {
      Component: App, // root layout route
      children: [
        {
          path: '/',
          Component: AppLayout,
          protected: true,
          children: appRoutes,
        },
      ],
    },
    {
      path: '*',
      Component: NotFoundPage,
    },
  ],
  {
    future: {
      v7_fetcherPersist: true,
    },
  }
);
